import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Box,
  Typography,
  Chip,
  Paper,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CalculateOutlinedIcon from '@mui/icons-material/CalculateOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { useLanguage } from '../../../utils/i18n';
import { determineIndexNameWithFallback, getEventSource } from '../../../helpers/howenScoreHelper';

const EVENT_KEY_I18N_MAP = {
  over_speed: 'driverScore.eventOverSpeed',
  idle_time: 'driverScore.eventIdleTime',
  harsh_braking: 'driverScore.eventHarshBraking',
  harsh_acceleration: 'driverScore.eventHarshAcceleration',
  yawning: 'driverScore.eventYawning',
  smoking: 'driverScore.eventSmoking',
  sleepy: 'driverScore.eventSleepy',
  distraction: 'driverScore.eventDistraction',
  phone_call: 'driverScore.eventPhoneCall',
  covering_camera: 'driverScore.eventCoveringCamera'
};

const formatEventKey = (key, t) => {
  if (!key) return '';
  const fallback = key
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
  const i18nKey = EVENT_KEY_I18N_MAP[key];
  if (i18nKey && typeof t === 'function') {
    return t(i18nKey, fallback);
  }
  return fallback;
};

const getScoreColor = (score) => {
  const numScore = Number(score) || 0;
  if (numScore >= 8) return '#2e7d32';
  if (numScore >= 5) return '#ff9800';
  return '#f44336';
};

const getIndexColor = (indexName) => {
  switch (String(indexName).toUpperCase()) {
    case 'A':
      return { bgcolor: '#e8f5e9', color: '#2e7d32' };
    case 'B':
      return { bgcolor: '#f0f4c3', color: '#827717' };
    case 'C':
      return { bgcolor: '#fff3e0', color: '#e65100' };
    case 'D':
      return { bgcolor: '#ffebee', color: '#c62828' };
    default:
      return { bgcolor: '#f5f5f5', color: '#616161' };
  }
};

function FractionBox({ numerator, denominator }) {
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        flexDirection: 'column',
        alignItems: 'center',
        verticalAlign: 'middle',
        mx: 0.75,
        px: 0.75
      }}
    >
      <Box
        component="span"
        sx={{
          borderBottom: '1.5px solid #334155',
          pb: 0.25,
          px: 0.75,
          fontSize: '0.82rem',
          fontWeight: 600,
          lineHeight: 1.2,
          textAlign: 'center'
        }}
      >
        {numerator}
      </Box>
      <Box
        component="span"
        sx={{
          pt: 0.25,
          px: 0.75,
          fontSize: '0.82rem',
          fontWeight: 600,
          lineHeight: 1.2,
          textAlign: 'center'
        }}
      >
        {denominator}
      </Box>
    </Box>
  );
}

FractionBox.propTypes = {
  numerator: PropTypes.node.isRequired,
  denominator: PropTypes.node.isRequired
};

export default function FormulaDialog({ open, onClose, formulaData }) {
  const { t, language } = useLanguage();

  if (!formulaData) return null;

  const {
    eventKey,
    eventCount = 0,
    totalRows = 0,
    weight = 0,
    score = 0,
    eventIndex = '-',
    eventIndexIsEstimated = false,
    eventIndexRoundedDirection = null,
    dateKey = '',
    vehicle = '-',
    scoreConfigName = '-',
    isRetaseDay = false,
    groupViolations = [],
    groupPctScore = null,
    groupIdxName = '-',
    scoreIndexes = [],
    vssTotalRows = null,
    fallbackWaypoints = 0
  } = formulaData;

  const localeStr = language === 'id' ? 'id-ID' : language === 'zh' ? 'zh-CN' : 'en-US';
  const roundedLabel = (dir) => (dir === 'up' ? t('driverScore.roundedUp', 'rounded up') : t('driverScore.roundedDown', 'rounded down'));

  const eventName = formatEventKey(eventKey, t);
  const source = getEventSource(eventKey);
  const sourceLabel = source === 'VSS' ? t('driverScore.sourceCamera', 'Camera (VSS)') : t('driverScore.sourceGps', 'GPS Telematics');
  const sourceChipBg = source === 'VSS' ? '#f3e5f5' : '#e3f2fd';
  const sourceChipColor = source === 'VSS' ? '#6a1b9a' : '#1565c0';

  const numCount = Number(eventCount) || 0;
  const numTotalRows = Number(totalRows) || 0;
  const numWeight = Number(weight) || 0;
  const numScore = Number(score) || 0;

  // Step 1: Rasio & Persentase Pelanggaran
  const rawRatio = numTotalRows > 0 ? numCount / numTotalRows : 0;
  const clampedRatio = Math.min(1, Math.max(0, rawRatio));
  const violationPercentage = parseFloat((clampedRatio * 100).toFixed(2));
  const isClampedMax = numTotalRows > 0 && numCount > numTotalRows;
  const isZeroRows = numTotalRows === 0;

  // Step 2: Skor Murni Parameter (0 - 10) & Persentase Kepatuhan (0% - 100%)
  const displayScore = Number.isInteger(numScore) ? numScore : parseFloat(numScore.toFixed(2));
  const compliancePercentage = parseFloat((displayScore * 10).toFixed(2));

  // Step 3: Nilai Parameter Berbobot (Kontribusi Poin)
  const weightedPoint = parseFloat(((displayScore / 10) * numWeight).toFixed(2));

  // Step 4: Akumulasi Seluruh Parameter pada Hari Tersebut
  const allEventsBreakdown = (groupViolations || []).map((ev) => {
    const isVss = ['sleepy', 'yawning', 'phone_call', 'distraction', 'smoking', 'covering_camera'].includes(ev.event_key);
    const evRows = Number((isVss ? (vssTotalRows ?? ev.total_rows) : ev.total_rows) ?? fallbackWaypoints ?? 0);
    const evCount = Number(ev.event_count || 0);
    const evWeight = Number(ev.weight || 0);
    const evSource = getEventSource(ev.event_key);

    let evScore = 0;
    if (evRows === 0) {
      evScore = 10;
    } else if (evSource === 'GPS') {
      evScore = ev.score != null ? Number(ev.score) : 0;
    } else if (ev.score != null) {
      evScore = Number(ev.score);
    } else {
      const r = Math.min(1, evCount / evRows);
      evScore = (1 - r) * 10;
    }

    const evDisplayScore = Number.isInteger(evScore) ? evScore : parseFloat(evScore.toFixed(2));
    const evWeightedPoint = parseFloat(((evDisplayScore / 10) * evWeight).toFixed(2));
    const idxRes = determineIndexNameWithFallback(evScore * 10, scoreIndexes);

    return {
      eventKey: ev.event_key,
      name: formatEventKey(ev.event_key, t),
      count: evCount,
      totalRows: evRows,
      weight: evWeight,
      score: evDisplayScore,
      weightedPoint: evWeightedPoint,
      idxName: idxRes.idx_name,
      isCurrent: ev.event_key === eventKey
    };
  });

  const totalWeightSum = allEventsBreakdown.reduce((sum, item) => sum + item.weight, 0);
  const totalWeightedPointsSum = parseFloat(allEventsBreakdown.reduce((sum, item) => sum + item.weightedPoint, 0).toFixed(2));
  const finalDailyScore = groupPctScore != null ? groupPctScore : totalWeightedPointsSum;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ m: 0, p: 2, bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                bgcolor: '#e3f2fd',
                color: '#1565c0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <CalculateOutlinedIcon />
            </Box>
            <Box>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <Typography variant="h6" fontWeight="bold">
                  {t('driverScore.formulaTitle', 'Transparansi Perhitungan Skor')}: {eventName}
                </Typography>
                <Chip
                  label={sourceLabel}
                  size="small"
                  sx={{
                    bgcolor: sourceChipBg,
                    color: sourceChipColor,
                    fontWeight: 600,
                    fontSize: '0.72rem',
                    height: 22
                  }}
                />
                <Chip
                  label={isRetaseDay ? t('driverScore.retase', 'Retase') : t('driverScore.nonRetase', 'Non Retase')}
                  size="small"
                  sx={{
                    fontWeight: 600,
                    fontSize: '0.72rem',
                    height: 22,
                    bgcolor: isRetaseDay ? '#e8f5e9' : '#fff3e0',
                    color: isRetaseDay ? '#2e7d32' : '#e65100'
                  }}
                />
              </Stack>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                {dateKey ? `${t('driverScore.dateLabel', 'Tanggal')}: ${dateKey} • ` : ''}
                {t('driverScore.vehicle', 'Kendaraan')}: {vehicle} • {t('driverScore.scoreConfig', 'Konfigurasi Skor')}: {scoreConfigName}
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={onClose} size="small" aria-label="close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 2.5 }}>
        <Stack spacing={2.5}>
          {/* 1. Ringkasan Nilai Data */}
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              borderRadius: 2,
              bgcolor: '#f8fafc',
              borderColor: '#e2e8f0'
            }}
          >
            <Typography variant="subtitle2" fontWeight="bold" color="text.primary" sx={{ mb: 1.5 }}>
              {t('driverScore.formulaSummaryTitle', 'Ringkasan Data Parameter')} ({eventName})
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} divider={<Divider orientation="vertical" flexItem />}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  {t('driverScore.totalViolations', 'Jumlah Pelanggaran')}
                </Typography>
                <Typography variant="h6" fontWeight="bold" color="text.primary">
                  {numCount.toLocaleString(localeStr)}{' '}
                  <Typography component="span" variant="caption">
                    {t('driverScore.formulaTimesUnit', 'kali')}
                  </Typography>
                </Typography>
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  {t('driverScore.formulaTotalWaypoints', 'Total Titik Perjalanan (Waypoint)')}
                </Typography>
                <Typography variant="h6" fontWeight="bold" color="text.primary">
                  {numTotalRows.toLocaleString(localeStr)}{' '}
                  <Typography component="span" variant="caption">
                    {t('driverScore.formulaDataPointsUnit', 'titik data')}
                  </Typography>
                </Typography>
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  {t('driverScore.formulaParameterWeight', 'Bobot Parameter')}
                </Typography>
                <Typography variant="h6" fontWeight="bold" color="text.primary">
                  {numWeight}%
                </Typography>
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  {t('driverScore.formulaParameterScoreScale', 'Skor Parameter (Skala 0–10)')}
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="h6" fontWeight="bold" sx={{ color: getScoreColor(displayScore) }}>
                    {displayScore}
                  </Typography>
                  <Chip
                    label={`${t('driverScore.indexWord', 'Indeks')} ${eventIndex}`}
                    size="small"
                    sx={{
                      fontWeight: 'bold',
                      height: 22,
                      fontSize: '0.72rem',
                      ...getIndexColor(eventIndex)
                    }}
                  />
                </Stack>
              </Box>
            </Stack>
          </Paper>

          {/* 2. Tahapan Perhitungan (Step a, b, c, d) */}
          <Box>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1.5 }}>
              {t('driverScore.formulaStepsTitle', 'Tahapan & Rumus Perhitungan')}
            </Typography>

            <Stack spacing={2}>
              {/* Tahap A */}
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Typography variant="subtitle2" fontWeight="bold" color="primary.main" gutterBottom>
                  {t('driverScore.formulaStepATitle', 'a. Persentase Pelanggaran Event (Rasio Pelanggaran)')}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  {t(
                    'driverScore.formulaStepADesc',
                    'Menghitung seberapa sering pelanggaran terjadi dibandingkan dengan seluruh titik perjalanan yang terekam:'
                  )}
                </Typography>

                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: 1.5,
                    bgcolor: '#f1f5f9',
                    fontFamily: 'inherit',
                    mb: 1.5,
                    overflowX: 'auto'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                    <Typography variant="body2" fontWeight={600}>
                      {t('driverScore.formulaViolationRatio', 'Rasio Pelanggaran')} =
                    </Typography>
                    <FractionBox
                      numerator={t('driverScore.totalViolations', 'Jumlah Pelanggaran')}
                      denominator={t('driverScore.formulaTripPointsShort', 'Total Titik Perjalanan')}
                    />
                    <Typography variant="body2" fontWeight={600} sx={{ ml: 1 }}>
                      • {t('driverScore.formulaViolationPct', 'Persentase Pelanggaran')} ={' '}
                      {t('driverScore.formulaViolationRatio', 'Rasio Pelanggaran')} × 100%
                    </Typography>
                  </Box>
                  <Divider sx={{ my: 1 }} />
                  {isZeroRows ? (
                    <Typography variant="body2" fontWeight="bold" color="success.main">
                      {t(
                        'driverScore.formulaZeroRowsResult',
                        'Hasil: Karena Total Titik Perjalanan = 0 (belum ada titik perjalanan), maka Rasio Pelanggaran dianggap 0% (Skor Penuh).'
                      )}
                    </Typography>
                  ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
                      <Typography variant="body2" fontWeight="bold">
                        {t('driverScore.formulaCalculationLabel', 'Perhitungan')}:{' '}
                      </Typography>
                      <FractionBox numerator={numCount} denominator={numTotalRows} />
                      <Typography variant="body2" fontWeight="bold">
                        × 100% = {violationPercentage}%
                      </Typography>
                      {isClampedMax && (
                        <Chip
                          label={t('driverScore.formulaClampedMaxChip', 'Dibatasi maks. 100% agar skor tidak minus')}
                          size="small"
                          color="warning"
                          sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                        />
                      )}
                    </Box>
                  )}
                </Box>
              </Paper>

              {/* Tahap B */}
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Typography variant="subtitle2" fontWeight="bold" color="primary.main" gutterBottom>
                  {t('driverScore.formulaStepBTitle', 'b. Skor Murni Parameter (Skala 0 – 10 & Persentase Kepatuhan)')}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  {t(
                    'driverScore.formulaStepBDesc',
                    'Sebelum dikalikan dengan bobot, sistem menghitung tingkat kepatuhan pengemudi pada parameter ini dalam skala 0 – 10 serta skala persentase 0% – 100%:'
                  )}
                </Typography>

                <Box sx={{ p: 1.5, borderRadius: 1.5, bgcolor: '#f1f5f9', overflowX: 'auto' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                    <Typography variant="body2" fontWeight={600}>
                      {t('driverScore.formulaParameterScore0to10', 'Skor Parameter (0–10)')} =
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      (1 −
                    </Typography>
                    <FractionBox
                      numerator={t('driverScore.totalViolations', 'Jumlah Pelanggaran')}
                      denominator={t('driverScore.formulaTripPointsShort', 'Total Titik Perjalanan')}
                    />
                    <Typography variant="body2" fontWeight={600}>
                      ) × 10
                    </Typography>
                  </Box>
                  <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                    {t('driverScore.formulaCompliancePct0to100', 'Persentase Kepatuhan (0%–100%)')} ={' '}
                    {t('driverScore.formulaParameterScoreShort', 'Skor Parameter')} × 10%
                  </Typography>
                  <Divider sx={{ my: 1 }} />
                  {isZeroRows ? (
                    <Typography variant="body2" fontWeight="bold" sx={{ color: getScoreColor(displayScore) }}>
                      {t('driverScore.formulaCalculationLabel', 'Perhitungan')}: (1 − 0) × 10 = {displayScore} (
                      {t('driverScore.formulaCompliancePctShort', 'Persentase Kepatuhan')}: {compliancePercentage}% →{' '}
                      {t('driverScore.indexWord', 'Indeks')} {eventIndex})
                    </Typography>
                  ) : (
                    <Stack spacing={0.5}>
                      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
                        <Typography variant="body2" fontWeight="bold">
                          {t('driverScore.formulaParameterScoreShort', 'Skor Parameter')} = (1 −
                        </Typography>
                        <FractionBox numerator={numCount} denominator={numTotalRows} />
                        <Typography variant="body2" fontWeight="bold">
                          ) × 10 ={' '}
                        </Typography>
                        <Typography variant="body2" fontWeight="bold" sx={{ color: getScoreColor(displayScore) }}>
                          {displayScore}
                        </Typography>
                      </Box>
                      <Typography variant="body2" fontWeight="bold">
                        {t('driverScore.formulaCompliancePctShort', 'Persentase Kepatuhan')} = {displayScore} × 10 = {compliancePercentage}%
                        → {t('driverScore.formulaIndexCategory', 'Kategori Indeks')}{' '}
                        <Box
                          component="span"
                          sx={{
                            px: 0.8,
                            py: 0.2,
                            borderRadius: 1,
                            ...getIndexColor(eventIndex)
                          }}
                        >
                          {eventIndex}
                        </Box>
                        {eventIndexIsEstimated && (
                          <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.75, fontStyle: 'italic' }}>
                            ({roundedLabel(eventIndexRoundedDirection)}{' '}
                            {t('driverScore.formulaRoundedToNearestIndex', 'ke rentang indeks terdekat')})
                          </Typography>
                        )}
                      </Typography>
                    </Stack>
                  )}
                </Box>
              </Paper>

              {/* Tahap C */}
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Typography variant="subtitle2" fontWeight="bold" color="primary.main" gutterBottom>
                  {t('driverScore.formulaStepCTitle', 'c. Nilai Parameter Berbobot (Kontribusi Poin)')}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  {t(
                    'driverScore.formulaStepCDesc1',
                    'Setiap parameter memiliki bobot (%) sesuai konfigurasi. Kontribusi poin dari parameter'
                  )}{' '}
                  <b>{eventName}</b> {t('driverScore.formulaStepCDesc2', 'terhadap nilai akhir adalah:')}
                </Typography>

                <Box sx={{ p: 1.5, borderRadius: 1.5, bgcolor: '#f1f5f9', overflowX: 'auto' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                    <Typography variant="body2" fontWeight={600}>
                      {t('driverScore.formulaWeightedParamValue', 'Nilai Parameter Berbobot')} =
                    </Typography>
                    <FractionBox numerator={t('driverScore.formulaParameterScoreShort', 'Skor Parameter')} denominator="10" />
                    <Typography variant="body2" fontWeight={600}>
                      × {t('driverScore.formulaParameterWeight', 'Bobot Parameter')}
                    </Typography>
                  </Box>
                  <Divider sx={{ my: 1 }} />
                  <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
                    <Typography variant="body2" fontWeight="bold">
                      {t('driverScore.formulaCalculationLabel', 'Perhitungan')} =
                    </Typography>
                    <FractionBox numerator={displayScore} denominator="10" />
                    <Typography variant="body2" fontWeight="bold">
                      × {numWeight} ={' '}
                    </Typography>
                    <Typography variant="body2" fontWeight="bold" color="primary.main">
                      {weightedPoint} {t('driverScore.formulaPointsUnit', 'poin')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      ({t('driverScore.formulaOutOfMax', 'dari maksimal')} {numWeight} {t('driverScore.formulaPointsUnit', 'poin')})
                    </Typography>
                  </Box>
                </Box>
              </Paper>

              {/* Tahap D */}
              {allEventsBreakdown.length > 0 && (
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                  <Typography variant="subtitle2" fontWeight="bold" color="primary.main" gutterBottom>
                    {t('driverScore.formulaStepDTitle', 'd. Total Skor Akhir (Penjumlahan Seluruh Parameter)')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    {t(
                      'driverScore.formulaStepDDesc',
                      'Total Skor Akhir diperoleh dengan menjumlahkan seluruh Nilai Parameter Berbobot dari setiap parameter yang aktif:'
                    )}
                  </Typography>

                  <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1.5 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#f8fafc' }}>
                          <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>
                            {t('driverScore.formulaColParameter', 'Parameter (Event)')}
                          </TableCell>
                          <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem' }} align="right">
                            {t('driverScore.violation', 'Pelanggaran')}
                          </TableCell>
                          <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem' }} align="right">
                            {t('driverScore.formulaTripPointsShort', 'Total Titik Perjalanan')}
                          </TableCell>
                          <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem' }} align="center">
                            {t('driverScore.formulaColPureScore', 'Skor Murni (0–10)')}
                          </TableCell>
                          <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem' }} align="center">
                            {t('driverScore.weight', 'Bobot')}
                          </TableCell>
                          <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem' }} align="right">
                            {t('driverScore.formulaColWeightedValue', 'Nilai Berbobot (Poin)')}
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {allEventsBreakdown.map((item) => (
                          <TableRow
                            key={item.eventKey}
                            sx={{
                              bgcolor: item.isCurrent ? 'rgba(25, 118, 210, 0.08)' : 'inherit'
                            }}
                          >
                            <TableCell sx={{ fontWeight: item.isCurrent ? 700 : 500, fontSize: '0.8rem' }}>
                              {item.name}
                              {item.isCurrent && (
                                <Chip
                                  label={t('driverScore.formulaSelectedChip', 'Dipilih')}
                                  size="small"
                                  color="primary"
                                  sx={{ ml: 1, height: 18, fontSize: '0.65rem', fontWeight: 600 }}
                                />
                              )}
                            </TableCell>
                            <TableCell align="right" sx={{ fontSize: '0.8rem' }}>
                              {item.count.toLocaleString(localeStr)}
                            </TableCell>
                            <TableCell align="right" sx={{ fontSize: '0.8rem' }}>
                              {item.totalRows.toLocaleString(localeStr)}
                            </TableCell>
                            <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.8rem', color: getScoreColor(item.score) }}>
                              {item.score}
                            </TableCell>
                            <TableCell align="center" sx={{ fontSize: '0.8rem' }}>
                              {item.weight}%
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.8rem' }}>
                              ({item.score} / 10) × {item.weight} = {item.weightedPoint}
                            </TableCell>
                          </TableRow>
                        ))}
                        <TableRow sx={{ bgcolor: '#f8fafc' }}>
                          <TableCell colSpan={4} sx={{ fontWeight: 'bold', fontSize: '0.82rem' }}>
                            {t('driverScore.formulaFinalTotalScore', 'Total Skor Akhir')}
                          </TableCell>
                          <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.82rem' }}>
                            {totalWeightSum}%
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '0.85rem', color: 'primary.main' }}>
                            <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center">
                              <span>{finalDailyScore}</span>
                              <Chip
                                label={`${t('driverScore.indexWord', 'Indeks')} ${groupIdxName}`}
                                size="small"
                                sx={{
                                  fontWeight: 'bold',
                                  height: 20,
                                  fontSize: '0.7rem',
                                  ...getIndexColor(groupIdxName)
                                }}
                              />
                            </Stack>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              )}
            </Stack>
          </Box>

          {/* 3. Legend / Keterangan Istilah */}
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              borderRadius: 2,
              bgcolor: '#fffbeb',
              borderColor: '#fde68a'
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <HelpOutlineIcon sx={{ fontSize: 18, color: '#b45309' }} />
              <Typography variant="subtitle2" fontWeight="bold" sx={{ color: '#92400e' }}>
                {t('driverScore.formulaLegendTitle', 'Keterangan Istilah (Legend)')}
              </Typography>
            </Stack>
            <Stack spacing={0.75} sx={{ pl: 0.5 }}>
              <Typography variant="caption" color="text.primary">
                • <b>{t('driverScore.totalViolations', 'Jumlah Pelanggaran')}</b>:{' '}
                {t(
                  'driverScore.formulaLegendViolationsDesc',
                  'Berapa kali pelanggaran terjadi dan tercatat oleh GPS atau Kamera selama kendaraan beroperasi.'
                )}
              </Typography>
              <Typography variant="caption" color="text.primary">
                • <b>{t('driverScore.formulaLegendWaypointsTitle', 'Total Titik Perjalanan (Waypoint / Total Baris)')}</b>:{' '}
                {t(
                  'driverScore.formulaLegendWaypointsDesc',
                  'Jumlah seluruh titik data perjalanan kendaraan yang terekam pada periode tersebut. Untuk kategori Retase, nilai ini dihitung dari total titik perjalanan dalam ritase aktif; untuk Kamera (VSS), diambil dari total baris rekaman perjalanan kamera.'
                )}
              </Typography>
              <Typography variant="caption" color="text.primary">
                • <b>{t('driverScore.formulaLegendPureScoreTitle', 'Skor Murni Parameter (0 – 10)')}</b>:{' '}
                {t(
                  'driverScore.formulaLegendPureScoreDesc',
                  'Tingkat kepatuhan pengemudi pada satu parameter sebelum dikalikan bobot. Skor 10 berarti sempurna (0 pelanggaran), dan tidak akan bernilai negatif meskipun jumlah pelanggaran melebihi titik perjalanan (batas maksimum rasio adalah 1).'
                )}
              </Typography>
              <Typography variant="caption" color="text.primary">
                • <b>{t('driverScore.formulaLegendWeightTitle', 'Bobot Parameter (%)')}</b>:{' '}
                {t(
                  'driverScore.formulaLegendWeightDesc',
                  'Persentase pengaruh suatu parameter terhadap nilai akhir sesuai pengaturan Konfigurasi Skor (total bobot seluruh parameter = 100%).'
                )}
              </Typography>
              <Typography variant="caption" color="text.primary">
                • <b>{t('driverScore.formulaLegendWeightedTitle', 'Nilai Parameter Berbobot (Poin)')}</b>:{' '}
                {t(
                  'driverScore.formulaLegendWeightedDesc',
                  'Poin kontribusi bersih parameter tersebut terhadap Total Skor Akhir (maksimal poin sama dengan nilai bobotnya).'
                )}
              </Typography>
              <Typography variant="caption" color="text.primary">
                • <b>{t('driverScore.formulaLegendIndexTitle', 'Indeks Skor (A / B / C / D)')}</b>:{' '}
                {t(
                  'driverScore.formulaLegendIndexDesc',
                  'Predikat nilai berdasarkan rentang skor (0 – 100) yang diatur pada konfigurasi skor perusahaan.'
                )}
              </Typography>
            </Stack>
          </Paper>

          {/* Rentang Indeks Skor Aktif jika tersedia */}
          {Array.isArray(scoreIndexes) && scoreIndexes.length > 0 && (
            <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, bgcolor: '#f8fafc' }}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                <Typography variant="caption" fontWeight="bold" color="text.secondary">
                  {t('driverScore.formulaIndexRangeLabel', 'Rentang Indeks')} ({scoreConfigName}):
                </Typography>
                {[...scoreIndexes]
                  .sort((a, b) => Number(b.max_idx ?? 0) - Number(a.max_idx ?? 0))
                  .map((idxItem, i) => (
                    <Chip
                      key={i}
                      size="small"
                      label={`${idxItem.idx_name}: ${idxItem.min_idx} – ${idxItem.max_idx}`}
                      sx={{
                        height: 20,
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        ...getIndexColor(idxItem.idx_name)
                      }}
                    />
                  ))}
              </Stack>
            </Paper>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 2.5, py: 1.5, bgcolor: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
        <Button onClick={onClose} variant="contained" size="small" sx={{ textTransform: 'none', borderRadius: 1.5 }}>
          {t('general.close', 'Tutup')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

FormulaDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  formulaData: PropTypes.object
};
